"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _reactRedux = require("react-redux");
var _toggleProgramSource = _interopRequireDefault(require("../program/toggleProgramSource"));
var _setSoloLive = _interopRequireDefault(require("../program/setSoloLive"));
var _StudioLogger = require("@studio/logger/StudioLogger");
var _checkSourceTypes = require("@studio/sources/checkSourceTypes");
var _getSourcesInProgram = require("@studio/sources/getSourcesInProgram");
var _getSources = require("@studio/sources/getSources");
var _sources = require("../../utils/sources");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
var LOG = new _StudioLogger.StudioLogger('takeGenericPresenterLive');

/**
 * Redux thunk action to add or remove a generic presenter source (camera or screen) to/from the program.
 * Handles camera enablement, mute status, solo live mode, and camera limit enforcement.
 *
 * @param {Object} source - The source object to add/remove from program
 * @param {string} source._id - Unique identifier for the source
 * @param {string} source.value - Attendee ID associated with the source
 * @param {string} source.type - Type of source (e.g., CAMERA_PRESENTER, SCREEN_PRESENTER)
 * @param {boolean} [source.cameraUnavailable] - Whether the camera is unavailable
 * @param {Object} [sourceActionConfig={}] - Configuration options for the action
 * @param {boolean} [sourceActionConfig.isSoloLive] - If true, sets this source as solo live (removing others)
 * @param {Function} setProgram - Function to update the program state
 * @param {Function} toggleAttendeeCameraStatus - Function to toggle attendee camera on/off
 * @param {Function} toggleAttendeeMuteStatus - Function to toggle attendee mute status
 * @returns {Function} Redux thunk function that dispatches appropriate actions based on configuration
 *
 */
function takeGenericPresenterLive(source) {
  var sourceActionConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var setProgram = arguments.length > 2 ? arguments[2] : undefined;
  var toggleAttendeeCameraStatus = arguments.length > 3 ? arguments[3] : undefined;
  var toggleAttendeeMuteStatus = arguments.length > 4 ? arguments[4] : undefined;
  return function (dispatch, getState) {
    var _broadcastSettings$au;
    var _getState = getState(),
      attendeeCameraStatus = _getState.attendeeCameraStatus,
      attendeeMuteStatus = _getState.attendeeMuteStatus,
      _getState$program$lay = _getState.program.layers,
      layers = _getState$program$lay === void 0 ? [] : _getState$program$lay,
      broadcastSettings = _getState.broadcastSettings,
      broadcastSources = _getState.broadcastSources,
      settings = _getState.broadcast.settings,
      _getState$meeting = _getState.meeting,
      meetingId = _getState$meeting.meetingId,
      attendeeTiles = _getState$meeting.attendeeTiles,
      _getState$isAudioMute = _getState.isAudioMuted,
      isAudioMuted = _getState$isAudioMute === void 0 ? undefined : _getState$isAudioMute;
    var autoMutePresenters = (_broadcastSettings$au = broadcastSettings === null || broadcastSettings === void 0 ? void 0 : broadcastSettings.autoMutePresenters) !== null && _broadcastSettings$au !== void 0 ? _broadcastSettings$au : settings === null || settings === void 0 ? void 0 : settings.autoMutePresenters;
    var isSoloLive = sourceActionConfig.isSoloLive;
    var sourceId = source._id;
    var isLiveSource = (0, _getSourcesInProgram.isSourceInProgram)(layers, sourceId);
    var sourceAction = isSoloLive ? _setSoloLive["default"] : _toggleProgramSource["default"];
    var isScreenShare = (0, _checkSourceTypes.isScreenPresenter)(source);
    var sourceValue = (0, _getSources.getSourceValue)(source);
    var isAttendeeCameraOn = attendeeCameraStatus[sourceValue] ? true : false;
    var isAttendeeMicOn = isAudioMuted !== undefined ? isAudioMuted : Boolean(attendeeMuteStatus === null || attendeeMuteStatus === void 0 ? void 0 : attendeeMuteStatus[sourceValue]);
    var addingSource = !layers.find(function (layer) {
      return layer.source._id === sourceId;
    });
    var sources = (0, _sources.getBroadcastSourcesFromLayers)(layers, broadcastSources === null || broadcastSources === void 0 ? void 0 : broadcastSources.sources);

    // Stage limit (10) - controls mute behavior
    var isStageAtLimit = addingSource ? (0, _sources.willExceedCameraLimit)(sources, source) : false;

    // Camera ON limit (15 Agora / 10 Chime) - controls camera turn-on
    var isCameraOnAtLimit = addingSource ? (0, _sources.hasMeetingExceededCameraLimit)(attendeeTiles, attendeeCameraStatus) : false;
    var removedSources = isSoloLive ? sources === null || sources === void 0 ? void 0 : sources.filter(function (src) {
      return src._id !== sourceId;
    }) : !addingSource ? [source] : [];
    removedSources === null || removedSources === void 0 || removedSources.forEach(function (src) {
      if ((0, _checkSourceTypes.isCameraPresenter)(src)) {
        LOG.info("ProgramUpdate: Attendee ".concat(src.value, " in meeting ").concat(meetingId, " is removed from the stage"));
      }
    });
    if (!isLiveSource && addingSource && !isStageAtLimit) {
      LOG.info("ProgramUpdate: Attendee ".concat(source.value, " in meeting ").concat(meetingId, " is added to the stage"));
    }

    // Default state of a presenter being taken live is unmuted
    var newDesiredMuteStatus = false;

    /**
     * This action can potentially dispatch many other actions, so we do them in batch for performance reasons (reduce render passes)
     */
    (0, _reactRedux.batch)(function () {
      /**
       * All of this stuff only applies if autoMutePresenters is turned on
       */
      if (autoMutePresenters) {
        /**
         * If the source is _not_ in program, set desired mute status to false (unmuted)
         */
        if (!isLiveSource) {
          newDesiredMuteStatus = false;
        } else if (!isSoloLive) {
          /**
           * If source is in the program, we are taking them out, so set the desired mute status to true (muted)
           */
          newDesiredMuteStatus = true;
        }

        // if we set a camera presenter solo, we want to mute the sources being removed from program.
        if (isSoloLive && (0, _checkSourceTypes.isCameraPresenter)(source)) {
          //why does this only apply to camera_presenter?? why not audio_presenter??
          layers.filter(function (layer) {
            return (0, _checkSourceTypes.isCameraPresenter)(layer.source) && layer.source._id !== source._id;
          }).forEach(function (layer) {
            var source = layer.source;
            var otherSourceValue = (0, _getSources.getSourceValue)(source);
            dispatch(toggleAttendeeMuteStatus(otherSourceValue, true));
          });
        }
      } else {
        newDesiredMuteStatus = isAttendeeMicOn;
      }
      if (isStageAtLimit || !autoMutePresenters) newDesiredMuteStatus = isAttendeeMicOn;

      /**
       * Set the desired attendee camera status to on if it is off.
       *
       *  Do this only if:
       *   - they are _not_ currently in the program
       *   - the source is _not_ a screen share source
       *   - the maximum camera is not reached
       */
      if (!isCameraOnAtLimit && !isAttendeeCameraOn && !isScreenShare && !isLiveSource && !source.cameraUnavailable) {
        dispatch(toggleAttendeeCameraStatus(sourceValue, true, setProgram));
      }
      dispatch(toggleAttendeeMuteStatus(sourceValue, newDesiredMuteStatus));
      dispatch(sourceAction(source, setProgram));
    });
  };
}
var _default = exports["default"] = takeGenericPresenterLive;